import { useCallback, useEffect, useState } from "react";
import { Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, frDate } from "@/lib/api";

const EMPTY = { name: "", gite_slug: "chalet-6", start: "", end: "", mode: "percent", percent: 20, price: "", weekend_price: "", min_nights: 2, active: true };

const SeasonRow = ({ r, onToggle, onEdit, onRemove }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-panel p-4"
    data-testid={`season-row-${r.id}`}>
    <div>
      <p className="text-sm font-semibold text-bone">
        {r.name} <span className="font-normal text-muted-foreground">· {r.gite_slug}</span>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {frDate(r.start)} → {frDate(r.end)} · {r.mode === "percent" ? `+${r.percent} %` : `${r.price || "—"} € / ${r.weekend_price || r.price || "—"} € week-end`} · {r.min_nights} nuit(s) min.
      </p>
    </div>
    <div className="flex items-center gap-3">
      <button onClick={() => onToggle(r)} data-testid={`season-toggle-${r.id}`}
        className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] ${r.active ? "bg-brass text-ink" : "border border-white/15 text-bone/72"}`}>
        {r.active ? "Active" : "Inactive"}
      </button>
      <button onClick={() => onEdit(r)} className="text-xs text-bone/70 underline" data-testid={`season-edit-${r.id}`}>Modifier</button>
      <button onClick={() => onRemove(r.id)} className="text-muted-foreground hover:text-red-400" data-testid={`season-delete-${r.id}`}>
        <Trash2 size={15} />
      </button>
    </div>
  </div>
);

export default function SeasonsTab({ gites }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/seasons").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const payload = () => ({
    ...f, percent: Number(f.percent) || 0, min_nights: Number(f.min_nights) || 1,
    price: f.price === "" ? null : Number(f.price),
    weekend_price: f.weekend_price === "" ? null : Number(f.weekend_price),
  });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/seasons/${editing}`, payload());
      else await api.post("/admin/seasons", payload());
      toast.success(editing ? "Période mise à jour" : "Période créée");
      setF(EMPTY); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggle = async (r) => {
    try {
      await api.put(`/admin/seasons/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/admin/seasons/${id}`); load(); };

  const presets = async () => {
    const { data } = await api.post("/admin/seasons/presets", {}, { params: { gite_slug: "chalet-6" } });
    toast.success(data.created ? `${data.created} période(s) ajoutée(s), à activer` : "Périodes déjà présentes");
    load();
  };

  return (
    <div data-testid="seasons-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-bone">Saisons & vacances</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Majoration appliquée automatiquement sur la période. Un prix fixé à la main sur le calendrier reste prioritaire.
          </p>
        </div>
        <button className="btn-ghost !py-2.5" data-testid="seasons-presets-btn" onClick={presets}>
          <Download size={15} /> Charger les vacances scolaires
        </button>
      </div>

      <form onSubmit={save} className="mt-6 grid gap-4 border border-white/10 bg-panel p-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="season-form">
        <label className="text-sm sm:col-span-2">
          <span className="mb-1.5 block text-xs text-bone">Nom de la période</span>
          <input required className="field !py-2" data-testid="season-name" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="ex : Vacances de Noël" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Gîte</span>
          <select className="field !py-2" data-testid="season-gite" value={f.gite_slug}
            onChange={(e) => setF({ ...f, gite_slug: e.target.value })}>
            {gites.map((g) => <option key={g.slug} value={g.slug}>{g.name}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Nuits minimum</span>
          <input type="number" min="1" className="field !py-2" data-testid="season-min-nights" value={f.min_nights}
            onChange={(e) => setF({ ...f, min_nights: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Du</span>
          <input type="date" required className="field !py-2" data-testid="season-start" value={f.start}
            onChange={(e) => setF({ ...f, start: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Au</span>
          <input type="date" required className="field !py-2" data-testid="season-end" value={f.end}
            onChange={(e) => setF({ ...f, end: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Type de majoration</span>
          <select className="field !py-2" data-testid="season-mode" value={f.mode}
            onChange={(e) => setF({ ...f, mode: e.target.value })}>
            <option value="percent">Pourcentage</option>
            <option value="fixed">Prix fixe à la nuit</option>
          </select>
        </label>
        {f.mode === "percent" ? (
          <label className="text-sm">
            <span className="mb-1.5 block text-xs text-bone">Majoration (%)</span>
            <input type="number" step="1" className="field !py-2" data-testid="season-percent" value={f.percent}
              onChange={(e) => setF({ ...f, percent: e.target.value })} />
          </label>
        ) : (
          <>
            <label className="text-sm">
              <span className="mb-1.5 block text-xs text-bone">Prix semaine (€)</span>
              <input type="number" className="field !py-2" data-testid="season-price" value={f.price}
                onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="ex : 250" />
            </label>
            <label className="text-sm">
              <span className="mb-1.5 block text-xs text-bone">Prix week-end (€)</span>
              <input type="number" className="field !py-2" data-testid="season-weekend-price" value={f.weekend_price}
                onChange={(e) => setF({ ...f, weekend_price: e.target.value })} placeholder="ex : 265" />
            </label>
          </>
        )}
        <div className="flex items-end gap-3 sm:col-span-2">
          <button className="btn-primary !py-2.5" data-testid="season-save">
            <Plus size={15} /> {editing ? "Enregistrer" : "Créer la période"}
          </button>
          {editing && (
            <button type="button" className="btn-ghost !py-2.5" data-testid="season-cancel-edit"
              onClick={() => { setEditing(null); setF(EMPTY); }}>Annuler</button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="seasons-list">
        {rows.length ? rows.map((r) => (
          <SeasonRow key={r.id} r={r} onToggle={toggle} onRemove={remove}
            onEdit={(row) => { setEditing(row.id); setF({ ...row, price: row.price ?? "", weekend_price: row.weekend_price ?? "" }); }} />
        )) : <p className="text-sm text-muted-foreground">Aucune période. Chargez les vacances scolaires ou créez la vôtre.</p>}
      </div>
    </div>
  );
}
